import { guessLang, isLang, type Lang } from '../i18n'
import { safeGet, safeSet } from './env'
import { parseRoute } from './routes'

export type Theme = 'dark' | 'light'
export type Audience = 'individuals' | 'business'

export interface AppState {
  lang: Lang
  theme: Theme
  cyber: boolean
  audience: Audience
}

type Listener = (state: AppState, prev: AppState) => void

const LANG_KEY = 'ct-lang'
const THEME_KEY = 'ct-theme'

function readInitialState(): AppState {
  const params = new URLSearchParams(location.search)
  const urlLang = params.get('lang')
  const savedLang = safeGet('local', LANG_KEY)
  const pathLang = parseRoute(location.pathname).lang
  const lang = pathLang ?? (isLang(urlLang) ? urlLang : isLang(savedLang) ? savedLang : guessLang())
  // The inline script in index.html already resolved the theme before first paint.
  const theme: Theme = document.documentElement.dataset.theme === 'light' ? 'light' : 'dark'
  const cyber = params.get('cyber') === '1'
  return { lang, theme, cyber, audience: 'individuals' }
}

class Store {
  private state: AppState
  private listeners = new Set<Listener>()

  constructor(initial: AppState) {
    this.state = initial
  }

  get(): AppState {
    return this.state
  }

  set(patch: Partial<AppState>): void {
    const prev = this.state
    const next = { ...prev, ...patch }
    if ((Object.keys(next) as (keyof AppState)[]).every((k) => next[k] === prev[k])) return
    this.state = next
    if (next.lang !== prev.lang) safeSet('local', LANG_KEY, next.lang)
    if (next.theme !== prev.theme) safeSet('local', THEME_KEY, next.theme)
    this.listeners.forEach((listener) => listener(next, prev))
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }
}

export const store = new Store(readInitialState())
